import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function scorePassword(pw) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score; // 0..5
}

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const emailFromUrl = params.get("email") || "";
  const tokenFromUrl = params.get("token") || "";

  const [email, setEmail] = useState(emailFromUrl);
  const [token, setToken] = useState(tokenFromUrl);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const missingLink = !emailFromUrl || !tokenFromUrl;
  const strength = useMemo(() => scorePassword(pwd), [pwd]);
  const strengthLabel = ["Very weak", "Weak", "Fair", "Good", "Strong", "Excellent"][strength];
  const canSubmit = email && token && pwd.length >= 8 && pwd === pwd2 && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email, token, new_password: pwd,
      });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not reset password. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6"
         style={{ background: "var(--miro-bg, #F5F7FB)" }}>
      <div className="max-w-md w-full bg-white border border-[var(--miro-border)] rounded-2xl p-8">
        <Link to="/login" data-testid="reset-back-link"
              className="inline-flex items-center gap-1 text-xs text-[var(--miro-text-muted)] hover:text-[var(--miro-navy)]">
          <ArrowLeft size={13}/> Back to sign in
        </Link>
        <div className="miro-overline mt-4">Set a new password</div>
        <h1 className="font-display font-black text-3xl tracking-tight text-[var(--miro-navy)]">
          Reset your password
        </h1>
        {missingLink && (
          <div data-testid="reset-missing-link" className="mt-3 text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded p-2">
            This link is incomplete. Please open the reset link from your email
            exactly as sent, or request a new one.
          </div>
        )}
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label>Email</Label>
            <Input
              data-testid="reset-email-input"
              type="email" required className="mt-1"
              value={email} onChange={(e) => setEmail(e.target.value)}
              readOnly={!!emailFromUrl}
            />
          </div>
          {!tokenFromUrl && (
            <div>
              <Label>Reset code</Label>
              <Input data-testid="reset-token-input"
                     required className="mt-1"
                     placeholder="Paste the code from your email"
                     value={token} onChange={(e) => setToken(e.target.value)}/>
            </div>
          )}
          <div>
            <Label>New password</Label>
            <Input data-testid="reset-pwd-input"
                   required type="password" className="mt-1"
                   value={pwd} onChange={(e) => setPwd(e.target.value)}
                   placeholder="Min 8 chars · mix of upper/lower/digit/symbol"/>
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-slate-200 rounded overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${(strength / 5) * 100}%`,
                    background: strength < 3 ? "#ef4444" : strength < 4 ? "#f59e0b" : "#10b981",
                  }}
                />
              </div>
              <span className="text-[10px] text-[var(--miro-text-muted)]">{strengthLabel}</span>
            </div>
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input data-testid="reset-pwd2-input"
                   required type="password" className="mt-1"
                   value={pwd2} onChange={(e) => setPwd2(e.target.value)}/>
            {pwd2 && pwd !== pwd2 && (
              <div className="mt-1 text-[11px] text-red-600">Passwords don't match yet.</div>
            )}
          </div>
          <Button
            data-testid="reset-submit-btn"
            type="submit"
            disabled={!canSubmit}
            className="w-full h-11 font-semibold"
            style={{ background: "var(--miro-navy)" }}
          >
            {submitting ? "Saving…" : (<><ShieldCheck size={15} className="mr-1.5"/> Set new password</>)}
          </Button>
        </form>
      </div>
    </div>
  );
}
